import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { partnerAdminService, personFileService } from '../services/api';
import {
  Users, Loader2, UserPlus, ShieldCheck, Copy, Check, Ban, RotateCcw, Trash2,
  Search, X, Building2, Clock, FileText, ChevronRight, Save, ExternalLink, Activity,
} from 'lucide-react';

const ALL_PERMS = ['VIEW_JOBS', 'SUBMIT_JOBS', 'EDIT_SUBMITTED_JOBS', 'UPLOAD_DOCUMENTS', 'VIEW_QUOTES', 'ACCEPT_QUOTES', 'VIEW_TRACKING', 'MANAGE_COMPANY_CONTACTS'];
const DEFAULT_PERMS = ['VIEW_JOBS', 'SUBMIT_JOBS', 'VIEW_QUOTES'];

const REQ_STATUS_COLORS = {
  SUBMITTED: 'bg-sky-100 text-sky-700', BLUELOCK_REVIEW: 'bg-indigo-100 text-indigo-700',
  QUOTING: 'bg-amber-100 text-amber-700', QUOTE_SHARED: 'bg-blue-100 text-blue-700',
  ACCEPTED: 'bg-emerald-100 text-emerald-700', DECLINED: 'bg-rose-100 text-rose-700',
  SCHEDULED: 'bg-cyan-100 text-cyan-700', IN_PROGRESS: 'bg-violet-100 text-violet-700',
  COMPLETED: 'bg-green-100 text-green-700',
};
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

const PartnerManagementPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: '', company_id: '', first_name: '', last_name: '', permissions: DEFAULT_PERMS });
  const [companies, setCompanies] = useState([]);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);
  const [search, setSearch] = useState('');

  // detail drawer
  const [detail, setDetail] = useState(null); // partner user
  const [activity, setActivity] = useState([]);
  const [requests, setRequests] = useState([]);
  const [permDraft, setPermDraft] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  const load = useCallback(async () => {
    try { const d = await partnerAdminService.listUsers(); setUsers(d.items || []); }
    catch (e) { if (e?.response?.status === 403) setNotAllowed(true); setUsers([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { personFileService.listCompanies('').then((d) => setCompanies(Array.isArray(d) ? d : (d.items || []))).catch(() => setCompanies([])); }, []);

  const togglePerm = (p) => setInvite((i) => ({ ...i, permissions: i.permissions.includes(p) ? i.permissions.filter((x) => x !== p) : [...i.permissions, p] }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null); setResult(null);
    try { const r = await partnerAdminService.invite(invite); setResult(r); setInvite({ email: '', company_id: '', first_name: '', last_name: '', permissions: DEFAULT_PERMS }); load(); }
    catch (e2) { setErr(e2?.response?.data?.detail || 'Invite failed.'); } finally { setSaving(false); }
  };

  const act = async (fn, id) => { try { await fn(id); load(); if (detail?.id === id) openDetail({ ...detail }); } catch { /* ignore */ } };
  const copyLink = () => { if (result?.activation_url) { navigator.clipboard?.writeText(window.location.origin + result.activation_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } };

  const openDetail = async (u) => {
    setDetail(u); setPermDraft(u.permissions || []); setActivity([]); setRequests([]); setDetailLoading(true);
    try {
      const [a, r] = await Promise.all([
        partnerAdminService.getActivity(u.id).catch(() => ({ items: [] })),
        u.company_id ? partnerAdminService.listRequests({ company_id: u.company_id }).catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
      ]);
      setActivity(a.items || []); setRequests(r.items || []);
    } finally { setDetailLoading(false); }
  };
  const closeDetail = () => setDetail(null);
  const togglePermDraft = (p) => setPermDraft((d) => (d.includes(p) ? d.filter((x) => x !== p) : [...d, p]));
  const savePerms = async () => {
    setSavingPerms(true);
    try { await partnerAdminService.updatePermissions(detail.id, permDraft); setDetail((d) => ({ ...d, permissions: permDraft })); load(); }
    catch { /* ignore */ } finally { setSavingPerms(false); }
  };
  const permsDirty = detail && JSON.stringify([...(permDraft || [])].sort()) !== JSON.stringify([...(detail.permissions || [])].sort());

  const filtered = (users || []).filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.company_name || '').toLowerCase().includes(q);
  });

  if (notAllowed) return <DashboardLayout><div className="max-w-2xl mx-auto text-center py-16" data-testid="partner-mgmt-forbidden"><ShieldCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" /><h2 className="text-lg font-semibold">Founder access required</h2><p className="text-gray-600 text-sm mt-1">Partner account management is restricted to the Founder (super admin application role).</p></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-5" data-testid="partner-management-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="w-7 h-7 text-blue-600" /> Partner Accounts</h1>
          <button onClick={() => setShowInvite((s) => !s)} data-testid="partner-invite-toggle" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"><UserPlus className="w-4 h-4" /> Invite partner</button>
        </div>
        <p className="text-sm text-gray-600">External, company-scoped business access. Partners never see internal cost, profit, margin, pricing floors, or internal notes.</p>

        {showInvite && (
          <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="partner-invite-form">
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><label className="block text-xs font-medium text-gray-600 mb-0.5">Email *</label><input type="email" required value={invite.email} onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} data-testid="partner-invite-email" className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-0.5">Company File *</label>
                  <select required value={invite.company_id} onChange={(e) => setInvite((i) => ({ ...i, company_id: e.target.value }))} data-testid="partner-invite-company" className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded">
                    <option value="">— Select company —</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-0.5">First name</label><input value={invite.first_name} onChange={(e) => setInvite((i) => ({ ...i, first_name: e.target.value }))} data-testid="partner-invite-firstname" className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-0.5">Last name</label><input value={invite.last_name} onChange={(e) => setInvite((i) => ({ ...i, last_name: e.target.value }))} data-testid="partner-invite-lastname" className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded" /></div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Permissions (least privilege)</label>
                <div className="flex flex-wrap gap-2">
                  {ALL_PERMS.map((p) => (
                    <label key={p} className={`text-[11px] px-2 py-1 rounded border cursor-pointer ${invite.permissions.includes(p) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-gray-200 text-gray-500'}`}>
                      <input type="checkbox" className="hidden" checked={invite.permissions.includes(p)} onChange={() => togglePerm(p)} data-testid={`partner-invite-perm-${p}`} /> {p.replace(/_/g, ' ')}
                    </label>
                  ))}
                </div>
              </div>
              {err && <p role="alert" className="text-sm text-rose-600" data-testid="partner-invite-error">{err}</p>}
              <button type="submit" disabled={saving} data-testid="partner-invite-submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Send invitation</button>
            </form>
            {result && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded p-3 text-sm" data-testid="partner-invite-result">
                <p className="text-green-800 font-medium">Invitation created. Deliver this activation link securely to the partner:</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-[11px] bg-white border rounded px-2 py-1 truncate flex-1" data-testid="partner-invite-link">{window.location.origin + result.activation_url}</code>
                  <button onClick={copyLink} className="inline-flex items-center gap-1 text-xs text-blue-700">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}</button>
                </div>
                <p className="text-[11px] text-green-700 mt-1">Expires {result.expires_at}.</p>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search partners, email, company…" data-testid="partner-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="partner-users-table">
          {!users ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
            filtered.length === 0 ? <p className="text-sm text-gray-500 p-4" data-testid="partner-users-empty">{search ? `No partners match “${search}”.` : 'No partner accounts yet.'}</p> : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs"><tr><th className="text-left px-3 py-2">Partner</th><th className="text-left px-3 py-2">Company</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Permissions</th><th className="text-right px-3 py-2">Actions</th></tr></thead>
                  <tbody className="divide-y">
                    {filtered.map((u) => (
                      <tr key={u.id} data-testid={`partner-user-row-${u.id}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2"><div className="font-medium text-gray-900">{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</div><div className="text-xs text-gray-500">{u.email}</div></td>
                        <td className="px-3 py-2 text-gray-700">{u.company_name || u.company_id || '—'}</td>
                        <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{u.partner_status || (u.is_active ? 'active' : 'inactive')}</span></td>
                        <td className="px-3 py-2 text-[11px] text-gray-500 max-w-[220px]">{(u.permissions || []).length} perm(s)</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => openDetail(u)} data-testid={`partner-manage-${u.id}`} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline mr-2"><ChevronRight className="w-3.5 h-3.5" /> Manage</button>
                          {u.is_active
                            ? <button onClick={() => act(partnerAdminService.deactivate, u.id)} data-testid={`partner-deactivate-${u.id}`} className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline mr-2"><Ban className="w-3.5 h-3.5" /> Deactivate</button>
                            : <button onClick={() => act(partnerAdminService.reactivate, u.id)} data-testid={`partner-reactivate-${u.id}`} className="inline-flex items-center gap-1 text-xs text-green-700 hover:underline mr-2"><RotateCcw className="w-3.5 h-3.5" /> Activate</button>}
                          <button onClick={() => act(partnerAdminService.revoke, u.id)} data-testid={`partner-revoke-${u.id}`} className="inline-flex items-center gap-1 text-xs text-rose-700 hover:underline"><Trash2 className="w-3.5 h-3.5" /> Revoke</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex" data-testid="partner-detail-drawer">
          <div className="flex-1 bg-black/40" onClick={closeDetail} />
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-start justify-between z-10">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{`${detail.first_name || ''} ${detail.last_name || ''}`.trim() || detail.email}</h2>
                <p className="text-xs text-gray-500">{detail.email}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] ${detail.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{detail.partner_status || (detail.is_active ? 'active' : 'inactive')}</span>
              </div>
              <button onClick={closeDetail} data-testid="partner-detail-close" className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-6">
              {/* Company cross-link */}
              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Company</h3>
                {detail.company_id ? (
                  <button onClick={() => navigate(`/admin/company/${detail.company_id}`)} data-testid="partner-detail-company-link"
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-200 text-left">
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-800"><Building2 className="w-4 h-4 text-blue-600" /> {detail.company_name || detail.company_id}</span>
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                  </button>
                ) : <p className="text-sm text-gray-400">No company on file.</p>}
                <dl className="mt-2 text-xs text-gray-500 space-y-1">
                  <div className="flex justify-between"><dt>Last login</dt><dd className="text-gray-700">{fmtDate(detail.last_login)}</dd></div>
                  <div className="flex justify-between"><dt>Invited / created</dt><dd className="text-gray-700">{fmtDate(detail.created_at)}</dd></div>
                </dl>
              </section>

              {/* Permissions editor */}
              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Permissions (least privilege)</h3>
                <div className="flex flex-wrap gap-2">
                  {ALL_PERMS.map((p) => (
                    <label key={p} className={`text-[11px] px-2 py-1 rounded border cursor-pointer ${permDraft.includes(p) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-gray-200 text-gray-500'}`}>
                      <input type="checkbox" className="hidden" checked={permDraft.includes(p)} onChange={() => togglePermDraft(p)} data-testid={`partner-detail-perm-${p}`} /> {p.replace(/_/g, ' ')}
                    </label>
                  ))}
                </div>
                {permsDirty && (
                  <button onClick={savePerms} disabled={savingPerms} data-testid="partner-detail-save-perms"
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                    {savingPerms ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save permissions
                  </button>
                )}
              </section>

              {/* Associated requests */}
              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Shipment Requests ({requests.length})</h3>
                {detailLoading ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> :
                  requests.length === 0 ? <p className="text-sm text-gray-400">No requests from this company yet.</p> : (
                    <div className="space-y-1.5">
                      {requests.slice(0, 8).map((r) => (
                        <button key={r.id} onClick={() => navigate(`/admin/partner-requests/${r.id}`)} data-testid={`partner-detail-request-${r.id}`}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-left">
                          <span className="min-w-0">
                            <span className="text-xs font-medium text-gray-800 block truncate">{(r.shipment?.reference_number) || r.id.slice(0, 8)} · {r.shipment?.pickup_address || 'origin'} → {r.shipment?.destination || 'dest'}</span>
                            <span className="text-[11px] text-gray-400">{fmtDate(r.created_at)}{r.document_count ? ` · ${r.document_count} doc(s)` : ''}</span>
                          </span>
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] whitespace-nowrap ${REQ_STATUS_COLORS[r.request_status] || 'bg-gray-100 text-gray-600'}`}>{(r.request_status || 'submitted').replace(/_/g, ' ')}</span>
                        </button>
                      ))}
                    </div>
                  )}
              </section>

              {/* Activity / history */}
              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Activity & History</h3>
                {detailLoading ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> :
                  activity.length === 0 ? <p className="text-sm text-gray-400">No recorded activity.</p> : (
                    <ul className="space-y-2" data-testid="partner-detail-activity">
                      {activity.slice(0, 12).map((a, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <Clock className="w-3.5 h-3.5 text-gray-300 mt-0.5 flex-shrink-0" />
                          <span><span className="font-medium text-gray-700">{(a.action || '').replace(/_/g, ' ').toLowerCase()}</span><span className="text-gray-400"> · {fmtDate(a.timestamp)}</span>{a.actor_email ? <span className="text-gray-400"> · {a.actor_email}</span> : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
              </section>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default PartnerManagementPage;
